
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Checkbox, Dialog, DialogContent, DialogActions, Card, CardContent, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Postrequestcall } from '../../request/Postrequest';
import { VERIFY_WORKBUDDY_INVITATION, FETCH_WORK_BUDDIES } from '../../constant/Apipath';
import { useSelector } from 'react-redux';
import BuildIDLogo from '../../assets/images/build_id_logo.png';

const WorkBuddyInvite = () => {
    const navigate = useNavigate();
    const [allowTexts, setAllowTexts] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [invitationData, setInvitationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const location = useLocation();
    const { loginData } = useSelector((state) => state?.main);
    useEffect(() => {
        // Extract userId and workBuddyId from the route
        const parts = location.pathname.split('/').filter(Boolean);
        const userId = parts[1] || '';
        const workBuddyId = parts[2] || '';

        if (userId && workBuddyId) {
            setLoading(true);
            // Fetch work buddy details to get names
            const obj = { SearchList: [{ userId: userId , id: workBuddyId }] };
            Postrequestcall(FETCH_WORK_BUDDIES, obj, loginData?.accessToken)
                .then((res) => {

                    const buddy = res?.data?.data?.find((item) => item?.id == workBuddyId);

                    if (buddy?.isVerified) {
                        navigate('/accepted', {
                            state: {
                                buddyName: buddy.name,
                                fullName: buddy.userName,
                                workLocation: buddy.workLocation,
                                description: buddy.description,
                                phoneNumber: buddy.phoneNo,
                                checkInInterval: buddy.checkInInterval,
                                isVerified: buddy.isVerified
                            },
                            replace: true
                        });
                        return;
                    }
                    if (buddy) {
                        setInvitationData({
                            buddyName: buddy.name,
                            fullName: buddy.userName,
                            lastName: buddy.lastName,
                            workLocation: buddy.workLocation,
                            description: buddy.description,
                            phoneNumber: buddy.phoneNo,
                            checkInInterval: buddy.checkInInterval
                        });
                    } else {
                        setInvitationData(null);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    setInvitationData(null);
                    setLoading(false);
                });
        } else {
            setInvitationData(null);
            setLoading(false);
        }
    }, [location.pathname, loginData]);


    const handleAcceptClick = async () => {
        if (!allowTexts) {
            setShowDialog(true);
        } else {
            const parts = location.pathname.split('/').filter(Boolean);
            const userId = parts[1] || '';
            const workBuddyId = parts[2] || '';
           
            const obj = { Id: workBuddyId, IsVerified: true };
            try {
                setSubmitting(true);
                await Postrequestcall(VERIFY_WORKBUDDY_INVITATION, obj, loginData?.accessToken);
                navigate('/accepted', {
                    state: {
                        buddyName: invitationData.buddyName,
                        fullName: invitationData.fullName,
                        workLocation: invitationData.workLocation,
                        description: invitationData.description,
                        phoneNumber: invitationData.phoneNumber,
                        checkInInterval: invitationData.checkInInterval,
                        isVerified: true
                    },
                    replace: true
                });
            } catch (error) {
                console.error('Error saving invitation:', error);
            }finally {
                setSubmitting(false);   
            }
        }
    };

    const handleDialogOk = () => {
        setShowDialog(false);
    };

    const handleInfoClick = (e) => {
        e.preventDefault();
        setShowInfoModal(true);
    };

    const handleInfoModalClose = () => {
        setShowInfoModal(false);
    };

    if (loading) {
        return (
            <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center">
                <Typography variant="h6" color="text.secondary">Loading invitation details...</Typography>
            </Box>
        );
    }

    if (!invitationData) {
        return (
            <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center">
                <Typography variant="h6" color="error">Unable to load invitation details</Typography>
            </Box>
        );
    }

    return (
        <Box
            minHeight="100vh"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="#f4f6f8"
            p={{ xs: 0.5, sm: 2 }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: { xs: 340, sm: 500 },
                    p: 0,
                    borderRadius: 4,
                    boxShadow: 4,
                    m: { xs: 1, sm: 0 }
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                    <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                        <img
                            src={BuildIDLogo}
                            alt="BuildID Logo"
                            style={{ width: 100, height: 'auto', marginBottom: 12 }}
                        />
                        <Typography
                            variant="h6"
                            gutterBottom
                            align="center"
                            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                        >
                            Hi {invitationData.buddyName}, <b>{invitationData.fullName}</b> has designated you to be their Work Buddy on BuildID.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        align="center"
                        mb={2}
                        sx={{ fontSize: { xs: '0.98rem', sm: '1.05rem' } }}
                    >
                        While working today, they will be prompted to check in using the BuildID app. If they miss a check-in, you will be notified by a text message.
                    </Typography>
                    <Box
                        display="flex"
                        alignItems="center"
                        mb={2}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        gap={{ xs: 1, sm: 0 }}
                    >
                        <Checkbox
                            checked={allowTexts}
                            onChange={(e) => setAllowTexts(e.target.checked)}
                            color="primary"
                            sx={{ p: 0, mr: { xs: 0, sm: 1 } }}
                        />
                        <Typography
                            variant="body2"
                            sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '0.95rem', sm: '1rem' } }}
                        >
                            Allow BuildID to send text messages to my number today
                        </Typography>
                        <IconButton size="small" onClick={handleInfoClick} sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 1, sm: 0 } }}>
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography
                        variant="body2"
                        align="center"
                        color="text.secondary"
                        mb={2}
                        sx={{ fontSize: { xs: '0.93rem', sm: '1rem' } }}
                    >
                        Please read our{' '}
                        <a href="https://www.iubenda.com/terms-and-conditions/90161377" target="_blank" rel="noreferrer" style={{ color: '#1976d2', fontWeight: 600 }}>Terms and Conditions</a> and{' '}
                        <a href="https://www.iubenda.com/privacy-policy/90161377" target="_blank" rel="noreferrer" style={{ color: '#1976d2', fontWeight: 600 }}>Privacy Policy</a> before using our services.
                    </Typography>
                    <Box display="flex" justifyContent="center" mt={2}>
                       {
                        submitting ? (
                            <Button variant="contained" color="primary" disabled  sx={{
                                minWidth: { xs: 100, sm: 120 },
                                fontWeight: 700,
                                fontSize: { xs: '0.98rem', sm: '1rem' },
                                borderRadius: 3,
                                boxShadow: '0 2px 8px 0 rgba(25, 118, 210, 0.10)'
                            }}>
                                Saving...
                            </Button>
                        ) : (
                             <Button
                            variant="contained"
                            color="primary"
                            disabled={!allowTexts || submitting}
                            onClick={handleAcceptClick}
                            sx={{
                                minWidth: { xs: 100, sm: 120 },
                                fontWeight: 700,
                                fontSize: { xs: '0.98rem', sm: '1rem' },
                                borderRadius: 3,
                                boxShadow: '0 2px 8px 0 rgba(25, 118, 210, 0.10)'
                            }}
                        >
                            Accept
                        </Button>
                        )
                       }
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={showInfoModal} onClose={handleInfoModalClose}>
                <DialogContent>
                    <Typography variant="body1" mb={2}>
                            Standard SMS rates may apply.<br /><br />
                            If {invitationData.fullName} uses Work Buddy on a different day, you will have to accept another invitation.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleInfoModalClose} variant="contained" color="primary">OK</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkBuddyInvite;
